import type { SystemPromptResponse } from '@malaberg/shared';
import { Box, Button, Group, Stack, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
/**
 * SystemPromptsTab — system_owner only.
 *
 * Single editor for the one merged system prompt ('main'). The legacy 'extra'
 * sub-tab was merged into 'main' in migration 20260514153000; this tab no
 * longer renders separate sub-tabs but the runtime still reads previous
 * versions via the version-history expander below the editor.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { systemPromptsApi } from '../../api/endpoints/system-prompts';
import { MarkdownEditor } from '../../components/settings/MarkdownEditor';
import { VersionHistoryExpander } from '../../components/settings/VersionHistoryExpander';
import type { VersionEntry } from '../../components/settings/VersionHistoryExpander';

export default function SystemPromptsTab() {
  const [prompt, setPrompt] = useState<SystemPromptResponse | null>(null);
  const [content, setContent] = useState('');
  const [versions, setVersions] = useState<VersionEntry[]>([]);
  const [saving, setSaving] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const loadPrompt = useCallback(async () => {
    try {
      const res = await systemPromptsApi.list();
      const response = res as {
        ok: boolean;
        prompts?: { main?: SystemPromptResponse | null };
        items?: SystemPromptResponse[];
      };
      const mainPrompt: SystemPromptResponse | null = response.prompts
        ? (response.prompts.main ?? null)
        : (response.items?.find((p) => p.name === 'main') ?? null);

      if (isMounted.current) {
        setPrompt(mainPrompt);
        setContent(mainPrompt?.content ?? '');
      }
    } catch {
      if (isMounted.current) {
        notifications.show({
          title: 'Error',
          message: 'Failed to load system prompt',
          color: 'red',
        });
      }
    }
  }, []);

  const loadVersions = useCallback(async () => {
    try {
      const res = await systemPromptsApi.getVersions('main');
      // The controller returns { ok, versions }; tolerate { items } too in case
      // a future caller normalises the shape.
      const response = res as
        | {
            ok?: boolean;
            versions?: VersionEntry[];
            items?: VersionEntry[];
          }
        | VersionEntry[];
      const items = Array.isArray(response)
        ? response
        : (response.versions ?? response.items ?? []);

      if (isMounted.current) {
        setVersions(items);
      }
    } catch {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    void loadPrompt();
    void loadVersions();
  }, [loadPrompt, loadVersions]);

  async function handleSave() {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await systemPromptsApi.update('main', { content });
      notifications.show({
        title: 'Prompt saved',
        message: 'System prompt has been saved.',
        color: 'green',
      });
      await loadPrompt();
      await loadVersions();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to save prompt',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleRevert(version: number) {
    await systemPromptsApi.revert('main', version);
    await loadPrompt();
    await loadVersions();
  }

  const currentVersionLabel = prompt ? ` (v${prompt.version})` : '';

  return (
    <Stack gap="md" data-testid="system-prompts-tab">
      <Title order={3}>System prompt{currentVersionLabel}</Title>

      <Stack gap="sm">
        <MarkdownEditor value={content} onChange={setContent} height="500px" />
        <Group justify="flex-end">
          <Button
            loading={saving}
            disabled={!content.trim() || saving}
            onClick={() => void handleSave()}
            data-testid="editor-save-btn"
          >
            Save
          </Button>
        </Group>
      </Stack>

      {versions.length > 0 && (
        <Box mt="md">
          <Title order={5} mb="sm">
            Version history
          </Title>
          <VersionHistoryExpander
            versions={versions}
            onRevert={(version) => handleRevert(version)}
            getPreview={(v) =>
              `Version ${v.version} — ${new Date(v.createdAt).toLocaleString()} by ${v.createdBy ?? 'system'}`
            }
          />
        </Box>
      )}
    </Stack>
  );
}
